// Product profile — lets one codebase + one build present as either
// OpenLabelMaker (labels) or OpenInvoiceMaker (invoices/documents). The product
// is chosen by the VITE_PRODUCT build flag (load-bearing: we deploy a separate
// per-domain bundle), with a hostname check only as a fallback for an unflagged
// bundle. Every product-specific literal reads from PRODUCT; the LABEL values are
// copied verbatim from the previous hardcoded strings so label output is
// byte-identical (all suites + audit stay green).

export type ProductId = 'label' | 'invoice'

/** Secondary-toolbar tools, by id. Data-only seam for now — step 1 does NOT gate
 *  the toolbar (both profiles enable the full set). */
export type ToolId =
  | 'select' | 'text' | 'shape' | 'barcode' | 'qr' | 'image'
  | 'table' | 'dataTable' | 'field' | 'align' | 'zoom'

const ALL_TOOLS: ToolId[] = ['select', 'text', 'shape', 'barcode', 'qr', 'image', 'table', 'dataTable', 'field', 'align', 'zoom']

export interface StartTabDef { id: string; label: string }

export interface ProductProfile {
  id: ProductId
  /** Plain-text brand name (document title, share copy). */
  name: string
  /** Footer brand label, e.g. "OpenLabelMaker.com". */
  brandDomainLabel: string
  /** Logo word parts: `${pre}<span class=accent>${mid}</span>${post}`. */
  logo: { pre: string; mid: string; post: string }
  /** Optional --color-brand override applied at boot; null = leave stylesheet default. */
  accentColor: string | null

  defaultSizeId: string
  defaultOrientation: 'landscape' | 'portrait'

  /** Enabled toolbar tools (gates the secondary-toolbar buttons; the devhooks
   *  API surface stays intact regardless so suites and power users keep it). */
  tools: ToolId[]

  /** Multi-page documents: shows the page-tab bar. Labels are single-page;
   *  the .olm v2 page model + devhooks stay available either way. */
  multiPage: boolean

  start: {
    searchPlaceholder: string
    tabs: StartTabDef[]
    /** When set, the size lists are narrowed to this category; null = today's
     *  behavior (everything except 'custom'). */
    sizeCategory: 'label' | 'document' | null
  }

  /** Marketing/app links (relative paths today — same-origin). */
  links: { pricing: string; contact: string; apiDocs: string; account: string }

  /** Continue-on-desktop / password-reset redirect URL. */
  editorUrl: string

  /** Default QR placeholder value. */
  qrPlaceholder: string

  /** Mobile share/email copy (kept verbatim per product for parity). */
  mobileShare: { title: string; text: string; emailSubject: string }

  /** Step-2 seam: who to cross-promote to. Unused by UI in step 1. */
  siblingProducts: { id: ProductId; name: string; url: string }[]
}

const LABEL: ProductProfile = {
  id: 'label',
  name: 'OpenLabelMaker',
  brandDomainLabel: 'OpenLabelMaker.com',
  logo: { pre: 'Open', mid: 'Label', post: 'Maker' },
  accentColor: null,
  defaultSizeId: 'dymo-30252',
  defaultOrientation: 'landscape',
  // Data Table (spreadsheet-style, built for invoices) is hidden for labels —
  // product decision 2026-08-21; batch labels use Table + Field instead.
  tools: ALL_TOOLS.filter((t) => t !== 'dataTable'),
  multiPage: false,
  start: {
    searchPlaceholder: 'Search brand, code, or size (e.g. L7160, DYMO, 21/sheet)...',
    tabs: [
      { id: 'recent', label: 'Recent' },
      { id: 'printers', label: 'Label Printers' },
      { id: 'sheets', label: 'Label Sheets' },
      { id: 'custom', label: 'Custom Size' },
    ],
    sizeCategory: null,
  },
  links: { pricing: '/pricing/', contact: '/contact/', apiDocs: '/docs/api/', account: '/account/' },
  editorUrl: 'https://openlabelmaker.com/editor/',
  qrPlaceholder: 'https://openlabelmaker.com',
  mobileShare: {
    title: 'My Label — OpenLabelMaker',
    text: 'I made a label with OpenLabelMaker',
    emailSubject: 'OpenLabelMaker — Continue on desktop',
  },
  siblingProducts: [{ id: 'invoice', name: 'OpenInvoiceMaker', url: 'https://openinvoicemaker.com/editor/' }],
}

const INVOICE: ProductProfile = {
  id: 'invoice',
  name: 'OpenInvoiceMaker',
  brandDomainLabel: 'OpenInvoiceMaker.com',
  logo: { pre: 'Open', mid: 'Invoice', post: 'Maker' },
  accentColor: null, // reuse the blue accent for step 1; a distinct color is a later step
  defaultSizeId: 'doc-a4',
  defaultOrientation: 'portrait',
  tools: ALL_TOOLS, // invoices use payment QR; packing slips use barcodes — keep the full set
  multiPage: true,
  start: {
    searchPlaceholder: 'Search a document size (A4, A5, Letter)...',
    tabs: [
      { id: 'recent', label: 'Recent' },
      { id: 'sheets', label: 'Templates' }, // reuses the sheets host; invoice templates are a later step
      { id: 'custom', label: 'Custom Size' },
    ],
    sizeCategory: 'document',
  },
  links: { pricing: '/pricing/', contact: '/contact/', apiDocs: '/docs/api/', account: '/account/' },
  editorUrl: 'https://openinvoicemaker.com/editor/',
  qrPlaceholder: 'https://openinvoicemaker.com',
  mobileShare: {
    title: 'My Invoice — OpenInvoiceMaker',
    text: 'I made an invoice with OpenInvoiceMaker',
    emailSubject: 'OpenInvoiceMaker — Continue on desktop',
  },
  siblingProducts: [{ id: 'label', name: 'OpenLabelMaker', url: 'https://openlabelmaker.com/editor/' }],
}

function resolveProfile(): ProductProfile {
  // Build flag is load-bearing (per-domain bundle); hostname is a fallback.
  const flag = (import.meta.env.VITE_PRODUCT as string | undefined)?.toLowerCase()
  if (flag === 'invoice') return INVOICE
  if (flag === 'label') return LABEL
  const host = typeof location !== 'undefined' ? location.hostname.toLowerCase() : ''
  if (host.includes('openinvoicemaker.com')) return INVOICE
  return LABEL
}

export const PRODUCT: ProductProfile = resolveProfile()

/** Logo HTML; the accent class is supplied per call site so each existing
 *  rendering (text-brand in the toolbar, text-blue-500 in start/mobile) is
 *  reproduced byte-for-byte. */
export function logoHtml(accentClass: string): string {
  const { pre, mid, post } = PRODUCT.logo
  return `${pre}<span class="${accentClass}">${mid}</span>${post}`
}

/** Apply product chrome at boot: document title + optional accent override.
 *  (Favicon swap is a later step once per-product assets exist under public/.) */
export function applyProductChrome(): void {
  if (typeof document === 'undefined') return
  // The label bundle keeps index.html's SEO title (that page ranks for
  // /editor/); only a non-label product rebrands the tab at runtime.
  if (PRODUCT.id !== 'label') document.title = `${PRODUCT.name} — Editor`
  if (PRODUCT.accentColor) document.documentElement.style.setProperty('--color-brand', PRODUCT.accentColor)
}
